import React from 'react';
import { Button } from '../Button';
import injectStores from '../RootStore/injectStores';
import type ServiceStore from 'stores/ServiceStore';
import type ViewStore from 'stores/ViewStore';
import type { RouterStore } from 'stores/RouterStore';

interface ErrorBoundaryProps {
  name: string;
  smallErrorMessage?: boolean;
  isLayout?: boolean;
  children?: React.ReactNode;
  logStore?: ServiceStore;
  viewStore?: ViewStore;
  routerStore?: RouterStore;
}

declare const window: Window;

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  static defaultProps = {
    smallErrorMessage: false,
    isLayout: false,
  };

  /**
   * ErrorBoundary
   * @param {object} props
   * @param {string} props.name
   */
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    // console.log('getDerivedStateFromError error', error);
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  reloadPage = () => {
    const { name, isLayout, logStore, viewStore, routerStore } = this.props;
    if (logStore) {
      const payload = {
        name,
        isLayout,
        path:
          routerStore?.currentPath ||
          window.location.pathname + window.location.search,
        appVersion: viewStore?.appVersion || 'Unknown',
      };
      logStore
        .create({
          level: 'error',
          appName: viewStore?.appName || 'Unknown IntraSync App',
          message: 'React Error Reload Page',
          payload: JSON.stringify(payload),
        })
        .then((result: unknown) => {
          console.log('created error', result);
        })
        .catch((err: unknown) =>
          console.error('Error creating error log', err)
        );
    }
    return window.location.reload();
  };

  retryPageLoading = (storageKey: string) => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.localStorage.getItem(storageKey) || 'false'
    );

    if (!pageHasAlreadyBeenForceRefreshed) {
      window.localStorage.setItem(storageKey, 'true');
      return this.reloadPage();
    } else {
      window.localStorage.setItem(storageKey, 'false');
    }
  };

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { name, logStore, viewStore, routerStore, isLayout } = this.props;
    // You can also log the error to an error reporting service
    const payload = {
      name,
      error,
      errorInfo,
      path:
        routerStore?.currentPath ||
        window.location.pathname + window.location.search,
      appVersion: viewStore?.appVersion || 'Unknown',
    };
    console.error('IntraSync Caught Error:', payload);
    if (logStore) {
      logStore
        .create({
          level: 'error',
          appName: viewStore?.appName || 'Unknown IntraSync App',
          message: 'React Error Caught',
          payload: JSON.stringify(payload),
        })
        .then((result: unknown) => {
          console.log('created error', result);
        })
        .catch((err: unknown) =>
          console.error('Error creating error log', err)
        );
    }
    if (isLayout && name.includes('ComponentRouter')) {
      this.retryPageLoading(
        (routerStore?.currentBasePath as string) || window.location.pathname
      );
    }
  }

  render() {
    const { routerStore, isLayout } = this.props;
    // console.log('state', this.state);
    if (this.state.hasError) {
      const { name, smallErrorMessage } = this.props;
      if (smallErrorMessage) {
        return <div>Oops</div>;
      }
      return (
        <div style={{ padding: isLayout ? 10 : undefined }}>
          <h4>Oops, something went wrong with {name}.</h4>
          <p>Error has been sent to the support team.</p>
          {isLayout && (
            <>
              <Button
                color="primary"
                onClick={() => {
                  routerStore?.goBack();
                }}
              >
                Go Back
              </Button>
              <Button
                color="secondary"
                onClick={() => {
                  this.reloadPage();
                }}
              >
                Reload Page
              </Button>
              <Button
                color="primary"
                onClick={() => {
                  routerStore?.goTo('Home');
                }}
              >
                Go Home
              </Button>
            </>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

const ErrorBoundaryExport = injectStores(ErrorBoundary);
export default ErrorBoundaryExport;
